import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { socket } from '../Containers/MainContainer';

// Utility functions
const numberWithCommas = (num) => num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// Skeleton Loading Component
const Skeleton = ({ className = "", variant = "default" }) => {
  const variants = {
    default: { background: '#21262d' },
    light: { background: '#30363d' },
    dark: { background: '#161b22' }
  };

  return (
    <div className={`animate-pulse rounded ${className}`} style={variants[variant] || variants.default}></div>
  );
};

// Enhanced Statistics Component
const StatCard = ({ title, value, subtitle, trend, trendValue, color = "blue", isLoading = false, icon }) => {
  const colorMap = {
    blue: { border: '#58a6ff', bg: 'rgba(88,166,255,0.15)' },
    green: { border: '#3fb950', bg: 'rgba(63,185,80,0.15)' },
    yellow: { border: '#d29922', bg: 'rgba(210,153,34,0.15)' },
    red: { border: '#f85149', bg: 'rgba(248,81,73,0.15)' },
    purple: { border: '#a855f7', bg: 'rgba(168,85,247,0.15)' },
    orange: { border: '#f97316', bg: 'rgba(249,115,22,0.15)' }
  };

  const c = colorMap[color] || colorMap.blue;

  return (
    <div style={{
      background: '#161b22',
      borderRadius: '12px',
      padding: '16px',
      border: '1px solid #21262d',
      borderLeft: `3px solid ${c.border}`,
      transition: 'all 0.3s'
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            {icon && <span style={{ fontSize: '18px' }}>{icon}</span>}
            <p style={{ color: '#8b949e', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.8px' }}>{title}</p>
          </div>
          {isLoading ? (
            <div style={{ width: '80px', height: '28px', background: '#21262d', borderRadius: '4px', marginBottom: '4px' }}></div>
          ) : (
            <p style={{ fontSize: '24px', fontWeight: '700', color: '#e6edf3', marginBottom: '4px' }}>{value}</p>
          )}
          {subtitle && (
            isLoading ? (
              <div style={{ width: '64px', height: '16px', background: '#21262d', borderRadius: '4px' }}></div>
            ) : (
              <p style={{ color: '#484f58', fontSize: '12px' }}>{subtitle}</p>
            )
          )}
        </div>
        {trend && !isLoading && (
          <div style={{
            fontSize: '12px',
            fontWeight: '500',
            padding: '4px 8px',
            borderRadius: '12px',
            background: trend === 'up' ? 'rgba(63,185,80,0.15)' : 'rgba(248,81,73,0.15)',
            color: trend === 'up' ? '#3fb950' : '#f85149'
          }}>
            <span>{trend === 'up' ? '^' : 'v'}</span>
            <span style={{ marginLeft: '4px' }}>{trendValue}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const Button = ({ variant = "primary", size = "md", onClick, children, className = "", disabled = false }) => {
  const variants = {
    primary: { background: '#238636', border: '1px solid #2ea043', color: '#ffffff' },
    secondary: { background: '#21262d', border: '1px solid #30363d', color: '#c9d1d9' },
    success: { background: '#238636', border: '1px solid #2ea043', color: '#ffffff' },
    danger: { background: '#da3633', border: '1px solid #f85149', color: '#ffffff' },
    warning: { background: '#9e6a03', border: '1px solid #d29922', color: '#ffffff' }
  };

  const sizes = {
    sm: { padding: '6px 12px', fontSize: '12px' },
    md: { padding: '8px 16px', fontSize: '14px' },
    lg: { padding: '12px 24px', fontSize: '16px' }
  };

  const v = variants[variant] || variants.primary;
  const s = sizes[size] || sizes.md;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={className}
      style={{
        ...v,
        ...s,
        borderRadius: '8px',
        fontWeight: '600',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s',
        outline: 'none'
      }}
    >
      {children}
    </button>
  );
};

const Select = ({ value, onChange, children, className = "", disabled = false }) => (
  <select
    value={value}
    onChange={onChange}
    disabled={disabled}
    className={className}
    style={{
      padding: '8px 16px',
      background: '#0d1117',
      border: '1px solid #30363d',
      borderRadius: '8px',
      color: '#e6edf3',
      fontSize: '14px',
      outline: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      transition: 'all 0.2s'
    }}
  >
    {children}
  </select>
);

const Input = ({ type = "text", placeholder, value, onChange, className = "", id, disabled = false, min, max }) => (
  <input
    type={type}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    id={id}
    disabled={disabled}
    min={min}
    max={max}
    className={className}
    style={{
      padding: '8px 16px',
      background: '#0d1117',
      border: '1px solid #30363d',
      borderRadius: '8px',
      color: '#e6edf3',
      fontSize: '14px',
      outline: 'none',
      opacity: disabled ? 0.5 : 1,
      cursor: disabled ? 'not-allowed' : 'text',
      transition: 'all 0.2s'
    }}
    onFocus={(e) => { e.target.style.borderColor = '#58a6ff' }}
    onBlur={(e) => { e.target.style.borderColor = '#30363d' }}
  />
);

const Table = ({ headers, children, isLoading = false, skeletonRows = 5 }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: '#161b22', borderBottom: '1px solid #21262d' }}>
          {headers.map((header, index) => (
            <th key={index} style={{
              padding: '12px 16px',
              textAlign: 'left',
              fontSize: '11px',
              fontWeight: '600',
              color: '#8b949e',
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              borderBottom: '1px solid #21262d'
            }}>
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          Array.from({ length: skeletonRows }, (_, index) => (
            <tr key={index} style={{ borderBottom: '1px solid #21262d' }}>
              {headers.map((_, cellIndex) => (
                <td key={cellIndex} style={{ padding: '16px' }}>
                  <div style={{ width: '100%', height: '16px', background: '#21262d', borderRadius: '4px' }}></div>
                </td>
              ))}
            </tr>
          ))
        ) : children}
      </tbody>
    </table>
  </div>
);

const Card = ({ title, children, className = "", headerActions, isLoading = false }) => (
  <div className={className} style={{
    background: '#161b22',
    borderRadius: '12px',
    border: '1px solid #21262d',
    overflow: 'hidden'
  }}>
    {title && (
      <div style={{ padding: '16px', borderBottom: '1px solid #21262d' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#e6edf3' }}>{title}</h3>
          {headerActions}
        </div>
      </div>
    )}
    <div style={{ padding: '16px' }}>{children}</div>
  </div>
);

// Pagination Component
const PaginationPluto = ({ pageNumber, currentPage = 1, requestNewList }) => {
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(pageNumber, startPage + 4);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(1)}
        disabled={currentPage === 1}
      >
        First
      </Button>

      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Prev
      </Button>

      {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(page => (
        <Button
          key={page}
          variant={page === currentPage ? "primary" : "secondary"}
          size="sm"
          onClick={() => requestNewList(page)}
        >
          {page}
        </Button>
      ))}

      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(currentPage + 1)}
        disabled={currentPage === pageNumber}
      >
        Next
      </Button>

      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(pageNumber)}
        disabled={currentPage === pageNumber}
      >
        Last
      </Button>
    </div>
  );
};

const OffersAnalyticsDashboard = () => {
  // State management
  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [selectedWall, setSelectedWall] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [itemsPerPage] = useState(20);

  // Data states
  const [offersData, setOffersData] = useState([]);
  const [chargebacksData, setChargebacksData] = useState([]);
  const [showChargebacks, setShowChargebacks] = useState(false);
  const [stats, setStats] = useState({
    totalOffers: 0,
    totalChargebacks: 0,
    totalCountries: 0,
    totalRevenue: 0,
    chargebackAmount: 0,
    avgOffersPerCountry: 0
  });

  // Available options from backend
  const [availableWalls, setAvailableWalls] = useState([
    { value: 'all', label: 'All Walls' }
  ]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);

  // Socket event handlers and data fetching
  useEffect(() => {
    const handlers = {
      'response_get_analytics_filters': (data) => {
        // Handle walls data - Use exact wall names as values
        if (data.walls && data.walls.length > 0) {
          const wallOptions = [
            { value: 'all', label: 'All Walls' },
            ...data.walls.map(wall => ({
              value: wall.wall_name,
              label: wall.wall_name
            }))
          ];

          setAvailableWalls(wallOptions);
        }

        // Handle months data
        if (data.months && data.months.length > 0) {
          const monthOptions = data.months.map(month => ({
            value: month.month.toString(),
            label: new Date(2024, month.month - 1).toLocaleString('default', { month: 'long' })
          }));
          setAvailableMonths(monthOptions);
        }

        // Handle years data
        if (data.years && data.years.length > 0) {
          const yearOptions = data.years.map(year => ({
            value: year.year.toString(),
            label: year.year.toString()
          }));
          setAvailableYears(yearOptions);

          // Set current year as default if available
          const currentYear = new Date().getFullYear().toString();
          if (yearOptions.find(y => y.value === currentYear)) {
            setSelectedYear(currentYear);
          } else if (yearOptions.length > 0) {
            setSelectedYear(yearOptions[0].value);
          }
        }

        // Set current month as default if available
        const currentMonth = (new Date().getMonth() + 1).toString();
        if (data.months && data.months.find(m => m.month.toString() === currentMonth)) {
          setSelectedMonth(currentMonth);
        } else if (data.months && data.months.length > 0) {
          setSelectedMonth(data.months[data.months.length - 1].month.toString());
        }

        setFiltersLoading(false);
      },

      'response_offers_analytics': (data) => {
        if (data.offers) {
          setOffersData(data.offers);

          // Calculate pagination
          if (data.totalCount) {
            setTotalPages(Math.ceil(data.totalCount / itemsPerPage));
          }
        }
        setLoading(false);
      },

      'response_offers_analytics_stats': (data) => {
        if (data.stats && data.stats.length > 0) {
          const statsData = data.stats[0];
          setStats({
            totalOffers: parseInt(statsData.total_offers) || 0,
            totalChargebacks: parseInt(statsData.total_chargebacks) || 0,
            totalCountries: parseInt(statsData.total_countries) || 0,
            totalRevenue: parseFloat(statsData.total_revenue) || 0,
            chargebackAmount: parseFloat(statsData.chargeback_amount) || 0,
            avgOffersPerCountry: statsData.total_countries > 0 ?
              (parseInt(statsData.total_offers) / parseInt(statsData.total_countries)).toFixed(1) : '0'
          });
        }
      },

      'response_chargebacks_analytics': (data) => {
        if (data.chargebacks) {
          setChargebacksData(data.chargebacks);

          // Update pagination for chargebacks view
          if (data.totalCount) {
            setTotalPages(Math.ceil(data.totalCount / itemsPerPage));
          }
        }
        setLoading(false);
      }
    };

    // Register event handlers
    Object.entries(handlers).forEach(([event, handler]) => {
      socket.on(event, handler);
    });

    // Fetch initial filter data
    socket.emit('getAnalyticsFilters');

    return () => {
      // Cleanup event handlers
      Object.keys(handlers).forEach(event => {
        socket.off(event);
      });
    };
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    if (selectedMonth && selectedYear && !filtersLoading) {
      setCurrentPage(1); // Reset to first page when filters change
      fetchOffersData(1);
      fetchStats();
    }
  }, [selectedWall, selectedMonth, selectedYear, searchTerm, filtersLoading]);

  const fetchOffersData = async (page = currentPage) => {
    setLoading(true);

    // Emit socket event to get analytics data with pagination
    socket.emit('getOffersAnalytics', {
      wall: selectedWall,
      month: selectedMonth,
      year: selectedYear,
      searchTerm: searchTerm,
      page: page - 1, // Backend expects 0-based pagination
      limit: itemsPerPage
    });
  };

  const fetchStats = async () => {
    // Emit socket event to get stats
    socket.emit('getOffersAnalyticsStats', {
      wall: selectedWall,
      month: selectedMonth,
      year: selectedYear,
      searchTerm: searchTerm
    });
  };

  const fetchChargebacksData = async (page = currentPage) => {
    setLoading(true);

    // Emit socket event to get chargebacks data with pagination
    socket.emit('getChargebacksAnalytics', {
      wall: selectedWall,
      month: selectedMonth,
      year: selectedYear,
      searchTerm: searchTerm,
      page: page - 1, // Backend expects 0-based pagination
      limit: itemsPerPage
    });
  };

  const handleToggleView = () => {
    setShowChargebacks(!showChargebacks);
    setCurrentPage(1);

    if (!showChargebacks) {
      // Switching to chargebacks view
      fetchChargebacksData(1);
    } else {
      // Switching to offers view
      fetchOffersData(1);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (showChargebacks) {
      fetchChargebacksData(page);
    } else {
      fetchOffersData(page);
    }
  };

  const handleJumpToPage = () => {
    const pageInput = document.getElementById('jumpToPage');
    const page = parseInt(pageInput.value);
    if (page && page > 0 && page <= totalPages) {
      handlePageChange(page);
      pageInput.value = '';
    }
  };

  // Generate chart data
  const chartData = offersData.reduce((acc, item) => {
    const existing = acc.find(x => x.country === item.country);
    if (existing) {
      existing.count += parseInt(item.count);
      existing.revenue += parseFloat(item.revenue);
    } else {
      acc.push({
        country: item.country,
        count: parseInt(item.count),
        revenue: parseFloat(item.revenue)
      });
    }
    return acc;
  }, []).sort((a, b) => b.count - a.count).slice(0, 10);

  const COLORS = ['#58a6ff', '#3fb950', '#d29922', '#f85149', '#a855f7', '#14b8a6', '#84CC16', '#f97316', '#f472b6', '#6366F1'];

  return (
    <div style={{ minHeight: '100vh', background: '#0d1117' }}>
      {/* Header */}
      <header style={{
        background: '#161b22',
        borderBottom: '1px solid #21262d',
        padding: '16px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '32px',
              height: '32px',
              background: 'rgba(88,166,255,0.15)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px'
            }}>
              <span>+</span>
            </div>
            <div>
              <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#e6edf3' }}>Offers Analytics Dashboard</h1>
              <p style={{ color: '#8b949e', fontSize: '13px' }}>Analyze offer performance by wall and time period</p>
            </div>
          </div>
          <div style={{ fontSize: '12px', color: '#484f58' }}>
            Last updated: {new Date().toLocaleTimeString()}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Filters Section */}
        <Card title="Filters" className="mb-8">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', alignItems: 'end' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#8b949e', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Search Offers</label>
              <div style={{ position: 'relative' }}>
                <Input
                  placeholder="Search by offer name..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full"
                  disabled={filtersLoading}
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#8b949e',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    x
                  </button>
                )}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#8b949e', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Offer Wall</label>
              <Select
                value={selectedWall}
                onChange={(e) => setSelectedWall(e.target.value)}
                className="w-full"
                disabled={filtersLoading}
              >
                {availableWalls.map(wall => (
                  <option key={wall.value} value={wall.value} style={{ background: '#0d1117', color: '#e6edf3' }}>{wall.label}</option>
                ))}
              </Select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#8b949e', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Month</label>
              <Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full"
                disabled={filtersLoading}
              >
                <option value="" style={{ background: '#0d1117', color: '#e6edf3' }}>Select Month</option>
                {availableMonths.map(month => (
                  <option key={month.value} value={month.value} style={{ background: '#0d1117', color: '#e6edf3' }}>{month.label}</option>
                ))}
              </Select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#8b949e', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Year</label>
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full"
                disabled={filtersLoading}
              >
                <option value="" style={{ background: '#0d1117', color: '#e6edf3' }}>Select Year</option>
                {availableYears.map(year => (
                  <option key={year.value} value={year.value} style={{ background: '#0d1117', color: '#e6edf3' }}>{year.label}</option>
                ))}
              </Select>
            </div>

            <div>
              <Button
                variant="secondary"
                onClick={handleClearSearch}
                disabled={!searchTerm && selectedWall === 'all'}
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </Card>

        {/* Statistics Cards */}
        {(selectedMonth && selectedYear) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '24px', marginTop: '24px' }}>
            <StatCard
              title="Total Offers"
              value={numberWithCommas(stats.totalOffers)}
              subtitle={`${selectedWall === 'all' ? 'All walls' : availableWalls.find(w => w.value === selectedWall)?.label} - ${availableMonths.find(m => m.value === selectedMonth)?.label} ${selectedYear}`}
              icon="+"
              color="blue"
              isLoading={loading}
            />
            <StatCard
              title="Chargebacks"
              value={numberWithCommas(stats.totalChargebacks)}
              subtitle="Total chargebacks"
              icon="!"
              color="red"
              isLoading={loading}
            />
            <StatCard
              title="Countries"
              value={stats.totalCountries}
              subtitle="Unique countries"
              icon="G"
              color="green"
              isLoading={loading}
            />
            <StatCard
              title="Revenue"
              value={`${numberWithCommas(stats.totalRevenue.toFixed(2))}`}
              subtitle="Total revenue"
              icon="$"
              color="yellow"
              isLoading={loading}
            />

          </div>
        )}

        {/* Data Table */}
        {(selectedMonth && selectedYear) && (
          <div style={{ marginTop: '24px' }}>
            <Card
              title={`${showChargebacks ? 'Chargebacks' : 'Offers'} Data - ${availableMonths.find(m => m.value === selectedMonth)?.label} ${selectedYear}`}
              headerActions={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                  <span style={{ fontSize: '13px', color: '#8b949e' }}>
                    {searchTerm && `Search: "${searchTerm}" | `}
                    Page {currentPage} of {totalPages} ({(showChargebacks ? chargebacksData : offersData).length} items)
                  </span>
                  <Button variant="secondary" size="sm" onClick={() => {
                    if (showChargebacks) {
                      fetchChargebacksData(currentPage);
                    } else {
                      fetchOffersData(currentPage);
                    }
                  }}>
                    Refresh
                  </Button>
                </div>
              }
            >
              <Table
                headers={showChargebacks ?
                  ['Offer Name', 'Country', 'CB Count', 'Wall'] :
                  ['Offer Name', 'Country', 'Completed', 'Chargebacks', 'Wall', 'Revenue']
                }
                isLoading={loading}
                skeletonRows={10}
              >
                {(showChargebacks ? chargebacksData : offersData).map((item, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #21262d', transition: 'background 0.15s' }}
                    onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(33,38,45,0.5)' }}
                    onMouseOut={(e) => { e.currentTarget.style.background = 'transparent' }}
                  >
                    <td style={{ padding: '16px', fontSize: '14px', color: '#e6edf3', maxWidth: '280px' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>
                        {item.name}
                      </div>
                    </td>
                    <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#c9d1d9' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <span>{item.country}</span>
                      </span>
                    </td>
                    {showChargebacks ? (
                      <>
                        <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#f85149', fontWeight: '500' }}>
                          {numberWithCommas(item.count)}
                        </td>
                        <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#c9d1d9' }}>
                          <span style={{ padding: '4px 8px', background: '#21262d', borderRadius: '12px', fontSize: '12px' }}>
                            {item.wall}
                          </span>
                        </td>
                        <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#f85149', fontWeight: '500' }}>
                          ${parseFloat(item.revenue || 0).toFixed(2)}
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#3fb950', fontWeight: '500' }}>
                          {numberWithCommas(item.count || 0)}
                        </td>
                        <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', fontWeight: '500' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            background: parseInt(item.chargeback_count || 0) > 0 ? 'rgba(248,81,73,0.15)' : '#21262d',
                            color: parseInt(item.chargeback_count || 0) > 0 ? '#f85149' : '#c9d1d9'
                          }}>
                            {numberWithCommas(item.chargeback_count || 0)}
                          </span>
                        </td>
                        <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#c9d1d9' }}>
                          <span style={{ padding: '4px 8px', background: '#21262d', borderRadius: '12px', fontSize: '12px' }}>
                            {item.wall}
                          </span>
                        </td>
                        <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#3fb950', fontWeight: '500' }}>
                          ${parseFloat(item.revenue || 0).toFixed(2)}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                  <PaginationPluto
                    pageNumber={totalPages}
                    currentPage={currentPage}
                    requestNewList={handlePageChange}
                  />

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Input
                      type="number"
                      placeholder="Page"
                      className="w-20"
                      id="jumpToPage"
                      min="1"
                      max={totalPages}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleJumpToPage}
                    >
                      Go
                    </Button>
                  </div>
                </div>
              )}

              {(showChargebacks ? chargebacksData : offersData).length === 0 && !loading && (
                <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>{showChargebacks ? '!' : '?'}</div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#e6edf3', marginBottom: '8px' }}>
                    No {showChargebacks ? 'chargebacks' : 'offers'} found
                  </h3>
                  <p style={{ color: '#8b949e' }}>
                    {searchTerm
                      ? `No ${showChargebacks ? 'chargebacks' : 'offers'} found matching "${searchTerm}". Try adjusting your search term or filters.`
                      : `No ${showChargebacks ? 'chargebacks' : 'offers'} found for the selected filters. Try adjusting your search criteria.`
                    }
                  </p>
                  {(searchTerm || selectedWall !== 'all') && (
                    <div style={{ marginTop: '16px' }}>
                      <Button variant="secondary" onClick={() => {
                        handleClearSearch();
                        setSelectedWall('all');
                      }}>
                        Clear All Filters
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Initial State */}
        {!selectedMonth || !selectedYear ? (
          <div style={{ marginTop: '24px' }}>
            <Card title="Get Started">
              <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>+</div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#e6edf3', marginBottom: '8px' }}>Select Filters to View Data</h3>
                <p style={{ color: '#8b949e', marginBottom: '24px' }}>
                  Choose an offer wall, month, and year to analyze offer performance and see detailed statistics.
                </p>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="primary"
                    onClick={() => socket.emit('getAnalyticsFilters')}
                    disabled={filtersLoading}
                  >
                    {filtersLoading ? 'Loading...' : 'Load Filter Options'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        ) : null}
      </main>
    </div>
  );
};

export default OffersAnalyticsDashboard;
